import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import { reportService } from '../../services/reportService';

interface GlobalMetricsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const GlobalMetricsModal: React.FC<GlobalMetricsModalProps> = ({ isOpen, onClose }) => {
    const [metrics, setMetrics] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setLoading(true);
            reportService.getGlobalMetrics()
                .then(data => setMetrics(data))
                .catch(err => console.error(err))
                .finally(() => setLoading(false));
        }
    }, [isOpen]);

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex justify-between items-center mb-6">
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                        Resumen Ejecutivo (Mes Actual)
                                    </Dialog.Title>
                                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100">
                                        <X size={20} className="text-gray-500" />
                                    </button>
                                </div>

                                {loading ? (
                                    <div className="flex justify-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                    </div>
                                ) : metrics ? (
                                    <div className="space-y-6">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-4 bg-blue-50 rounded-lg">
                                                <p className="text-sm text-blue-600 font-medium">Ventas Totales</p>
                                                <p className="text-2xl font-bold text-gray-900">${metrics.sales.total.toLocaleString()}</p>
                                            </div>
                                            <div className="p-4 bg-green-50 rounded-lg">
                                                <p className="text-sm text-green-600 font-medium">Transacciones</p>
                                                <p className="text-2xl font-bold text-gray-900">{metrics.sales.transactions}</p>
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <h4 className="font-medium text-gray-800 mb-2">Modelo Estrella</h4>
                                            {metrics.sales.bestSellingModel ? (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-600">{metrics.sales.bestSellingModel.code}</span>
                                                    <span className="font-bold text-gray-900">{metrics.sales.bestSellingModel.count} unidades</span>
                                                </div>
                                            ) : (
                                                <p className="text-gray-500 text-sm">No hay suficientes datos</p>
                                            )}
                                        </div>

                                        <div className="border-t pt-4">
                                            <h4 className="font-medium text-gray-800 mb-3">Rendimiento Modistas (Tickets Completados)</h4>
                                            <ul className="space-y-2">
                                                {metrics.staff.ticketsBySeamstress.length > 0 ? (
                                                    metrics.staff.ticketsBySeamstress.map((t: any, idx: number) => (
                                                        <li key={idx} className="flex justify-between text-sm">
                                                            <span className="text-gray-600">{t.seamstressName || 'Sin asignar'}</span>
                                                            <span className="font-semibold">{t._count.id} tickets</span>
                                                        </li>
                                                    ))
                                                ) : (
                                                    <li className="text-sm text-gray-500">Sin tickets completados este mes</li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">No se pudieron cargar los datos.</div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default GlobalMetricsModal;
